import { type ReactNode } from 'react';
import { Controller, useFormContext, type FieldErrors, type Resolver } from 'react-hook-form';
import { composeFormSchema, type BidFormField, type BidFormId } from '@tempmee/awards';
import { Checkbox, Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';

type BidFormValues = Record<string, unknown>;

// Full Award Terms & Conditions consent copy (mirrors the `@tempmee/awards`
// `awardTerms` field label). Kept here because the office-application descriptor
// carries no `awardTerms` field to source it from, and the checkbox is shared
// by both the application and EOD-feedback forms.
const BID_TERMS_LABEL =
  'I confirm that I have read and agree to the Best in Dental Award Terms & Conditions, including eligibility requirements, nomination criteria, and the use of submitted information for promotional purposes.';

// The sub-phrase that points at the T&C document. No public T&C URL exists yet;
// until one does, the phrase renders as a styled (non-link) span so it isn't
// announced as a link-to-nowhere. Set BID_TERMS_URL and the phrase becomes a
// real external anchor whose click opens the doc WITHOUT toggling the checkbox.
const BID_TERMS_LINK_PHRASE = 'Best in Dental Award Terms & Conditions';
const BID_TERMS_URL = '';

/**
 * Renders a consent label with the Best in Dental Award T&C phrase styled as a
 * teal link. Shared by the hardcoded application/EOD terms checkbox
 * (`BidTermsCheckbox`, which uses the default full label) and the generic
 * descriptor-driven boolean field renderer (which passes the field's own
 * label). When the phrase isn't present the plain string is returned, so
 * non-terms boolean fields are unaffected. No public T&C URL exists yet, so the
 * phrase renders as a styled (non-link) span rather than a link-to-nowhere; set
 * BID_TERMS_URL and it becomes a real external anchor whose click opens the doc
 * WITHOUT toggling the checkbox.
 */
export const renderBidTermsLabel = (label: string = BID_TERMS_LABEL): ReactNode => {
  const parts = label.split(BID_TERMS_LINK_PHRASE);
  if (parts.length !== 2) {
    return label;
  }
  const [before, after] = parts;
  return (
    <Typography as="span" color={colors.text.primary}>
      {before}
      {BID_TERMS_URL ? (
        <a
          href={BID_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary-500 underline"
          onClick={(event) => event.stopPropagation()}
        >
          {BID_TERMS_LINK_PHRASE}
        </a>
      ) : (
        <Typography as="span" className="text-brand-primary-500 underline">
          {BID_TERMS_LINK_PHRASE}
        </Typography>
      )}
      {after}
    </Typography>
  );
};

/**
 * Seeds react-hook-form defaults: `''` for every question field, plus any
 * top-level submit fields (mailing address, terms) passed in `extra`.
 */
export const buildBidDefaultValues = (
  fields: readonly BidFormField[],
  extra: BidFormValues = {},
): BidFormValues => ({
  ...Object.fromEntries(fields.map((field) => [field.name, ''])),
  ...extra,
});

/**
 * Hand-rolled RHF `Resolver` over a composed BID zod schema. `@tempmee/awards`
 * resolves its own zod, which can brand its types with a different minor than
 * the app's install — so passing the schema straight into `zodResolver` fails
 * TS overload resolution even though the two copies are runtime-compatible.
 * Adapting to RHF's `Resolver` shape by hand sidesteps that coupling.
 *
 * The schema is composed once per resolver (memoize the resolver on `fields` at
 * the call site so it isn't rebuilt every render). `extraValidate` covers the
 * top-level submit fields that aren't questions in the schema (mailing address,
 * terms checkbox).
 */
export const buildBidFormResolver = (
  id: BidFormId,
  fields: readonly BidFormField[],
  extraValidate?: (values: BidFormValues) => FieldErrors<BidFormValues>,
): Resolver<BidFormValues> => {
  const schema = composeFormSchema({ id, fields });

  return (values) => {
    const result = schema.safeParse(values);

    const errors: FieldErrors<BidFormValues> = {};
    if (!result.success) {
      for (const issue of result.error.issues) {
        const key = issue.path.join('.') || 'root';
        errors[key] = { type: issue.code, message: issue.message };
      }
    }
    if (extraValidate) {
      Object.assign(errors, extraValidate(values));
    }

    if (Object.keys(errors).length > 0) {
      return { values: {}, errors };
    }
    return { values: { ...values, ...(result.success ? result.data : {}) }, errors: {} };
  };
};

export const BID_TERMS_REQUIRED_MESSAGE =
  'You must accept the Best in Dental Award Terms & Conditions.';

/**
 * Validates a string-boolean terms-consent field — a top-level (non-question)
 * submit field shared by the application and EOD-feedback forms. Returns an RHF
 * error map keyed by `name` when the field isn't the accepted `'true'`, else an
 * empty map. Attesting to unchecked consent is a compliance bug (and the server
 * 400s on it), so both forms MUST gate on this.
 */
export const validateBidTermsField = (
  name: string,
  values: BidFormValues,
): FieldErrors<BidFormValues> =>
  values[name] === 'true'
    ? {}
    : { [name]: { type: 'required', message: BID_TERMS_REQUIRED_MESSAGE } };

/**
 * Stringifies the answer values for submission, excluding the top-level submit
 * fields (mailing address, terms) — the server rejects unknown answer keys, and
 * those fields are sent separately.
 */
export const buildBidAnswers = (
  values: BidFormValues,
  excludeKeys: string[],
): Record<string, string> => {
  const answers: Record<string, string> = {};
  for (const [key, val] of Object.entries(values)) {
    if (!excludeKeys.includes(key)) {
      answers[key] = String(val ?? '');
    }
  }
  return answers;
};

/** Inline field validation error caption, keyed `bid-field-<name>-error`. */
export const BidFieldError = ({ name, message }: { name: string; message?: string }) =>
  message ? (
    <Typography variant="caption" color={colors.error[500]} data-cy={`bid-field-${name}-error`}>
      {message}
    </Typography>
  ) : null;

/**
 * The Award Terms & Conditions checkbox — a top-level (non-question) submit
 * field shared by the application and EOD-feedback forms. Stored as a
 * string-boolean (`'true'`/`''`) so it flows through the same RHF value map as
 * the question answers. Reads the ambient form context, so it must sit inside a
 * `FormProvider`.
 */
export const BidTermsCheckbox = ({ name, dataCy }: { name: string; dataCy: string }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Column gap={1} data-cy={`bid-field-${name}`}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={field.value === 'true'}
            onChange={(checked) => field.onChange(checked ? 'true' : '')}
            label={renderBidTermsLabel()}
            error={Boolean(errors[name])}
            className="items-start"
            data-cy={dataCy}
          />
        )}
      />
      <BidFieldError name={name} message={errors[name]?.message as string | undefined} />
    </Column>
  );
};
